import {useMediaQuery} from '@chakra-ui/react';
import {isEmpty} from 'lodash';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  Col,
  UiBox,
  UiButton,
  UiFormControl,
  UiFormErrorMessage,
  UiImage,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
  UiTextarea,
  UiToggleButton,
} from '~/components/elements';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {QUERY_MOBILE} from '~/constants';
import {MENSTRUAL_HEALTH_KEYS} from '~/constants/medical-questionnaires';
import {useInput, useNotification} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const ENTER_MY_OWN = 'ENTER_MY_OWN_DESCRIPTION';
const NO_SYMPTOMS = 'NO_SYMPTOMS';

const Questionnaire8Screen = () => {
  const [isDesktop] = useMediaQuery(`(min-width: ${QUERY_MOBILE})`);
  const dispatch: AppDispatch = useDispatch();
  const {addMessage, addValidationMessage} = useNotification(dispatch);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[MENSTRUAL_HEALTH_KEYS.SELECT_SYMPTOMS]?.data,
  );

  const {questionnaires} = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const questionnaire = questionnaires?.find(i => i.key === MENSTRUAL_HEALTH_KEYS.SELECT_SYMPTOMS);

  const rules = {
    symptomOther: (attributes: any) => {
      return {
        presence: {allowEmpty: false, message: `^This field can't be blank`},
        length: {
          minimum: 5,
          tooShort: '^This field is too short (minimum is 5 characters)',
          tokenizer: function (value: any) {
            return trimSpaces(value);
          },
        },
      };
    },
  };

  const defaultOptions = questionnaire?.options?.reduce<Record<string, boolean>>((res, cur) => {
    res[cur.value] = currentAnswer?.selectedOptions?.includes(cur.value) ? true : false;
    return res;
  }, {});

  const resetOptions = questionnaire?.options?.reduce<Record<string, boolean>>((res, cur) => {
    res[cur.value] = false;

    return res;
  }, {});

  const {inputs, setInputs, validation, trimSpaces} = useInput(
    {
      defaultOptions,
      selectedOption: currentAnswer?.selectedOption || [],
      selectedSymptomOther: currentAnswer?.selectedSymptomOther || '',
      symptomOther: currentAnswer?.symptomOther || '',
    },
    {rules},
  );

  // @ts-ignore
  const dataSubmit = Object.keys(inputs.defaultOptions).filter(i => inputs?.defaultOptions?.[i] === true);

  const onClickOption = (answer: {value: string | number; label: string}) => {
    const {value} = answer;
    setInputs({defaultOptions: {...inputs.defaultOptions, [value]: !inputs?.defaultOptions?.[value]}});

    if (inputs.selectedSymptomOther === NO_SYMPTOMS) {
      setInputs({selectedSymptomOther: ''});
    }
  };

  const onClickSelectedSymptomOther = (answer: {value: any; label: string}) => {
    const {value} = answer;
    if (value === NO_SYMPTOMS) {
      setInputs({defaultOptions: resetOptions, selectedOption: [], selectedSymptomOther: value, symptomOther: ''});
    } else {
      setInputs({selectedSymptomOther: value});
    }
  };

  const onClickSubmit = async () => {
    if (inputs.selectedSymptomOther === ENTER_MY_OWN) {
      const errors = validation.getErrors();
      if (errors) {
        return;
      }
    }
    dispatch(
      setQuestionnaireAnswer({
        key: MENSTRUAL_HEALTH_KEYS.SELECT_SYMPTOMS,
        index: questionnaire?.index || '',
        data: {
          selectedOptions: dataSubmit || [],
          symptomOther: inputs.symptomOther && inputs.symptomOther.length ? inputs.symptomOther.trim() : '',
          selectedSymptomOther: inputs.selectedSymptomOther,
        },
      }),
    );
    dispatch(setNextQuestionnaire({value: MENSTRUAL_HEALTH_KEYS.HOW_TO_DEAL}));
  };

  const renderSymptomInput = () => {
    if (inputs.selectedSymptomOther === ENTER_MY_OWN) {
      return (
        <UiBox w="100%" mt={spacing.small} className="animate__animated animate__fadeInLeft">
          <UiFormControl id="symptomOther" isRequired isInvalid={!!validation?.errors?.symptomOther}>
            <UiTextarea
              bg="white"
              borderRadius={spacing.tiny}
              placeholder="Type your response here..."
              defaultValue={inputs.symptomOther}
              onChange={({target}) => setInputs({symptomOther: target.value})}
              minH="120px"
            />
            {validation?.errors?.symptomOther && (
              <UiFormErrorMessage>{validation?.errors?.symptomOther[0]}</UiFormErrorMessage>
            )}
          </UiFormControl>
        </UiBox>
      );
    }
    return null;
  };

  return (
    <>
      <UiQuestionnaireLayout
        backIcon
        disableNext={!dataSubmit.length && !inputs.selectedSymptomOther}
        handleOnNext={onClickSubmit}
        actions={questionnaire?.actions}
        checkoutStep={questionnaire?.checkoutStep}
      >
        <Col alignItems="center" textAlign="center">
          <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.small} />
          <UiText fontSize="xl" fontWeight="bold" mt={spacing.medium}>
            {questionnaire?.questions?.default}
          </UiText>
          <UiSimpleGrid columns={3} spacing={{base: 2, lg: 5}} mt={spacing.small}>
            {questionnaire?.options?.map(value => (
              <UiBox key={`${value.label}-${value.value}`}>
                <UiToggleButton
                  selected={inputs?.defaultOptions?.[value.value] || false}
                  value={value}
                  onClick={() => onClickOption(value)}
                  labelFontSize={12}
                />
              </UiBox>
            ))}
          </UiSimpleGrid>
          <UiSimpleGrid columns={2} spacing={5} mt={spacing.small}>
            {questionnaire?.symptomOther?.map(value => (
              <ScrollToBottomButton
                key={`${value.label}-${value.key}`}
                title={value.label}
                variant={inputs.selectedSymptomOther === value.key ? 'solid' : 'toggle'}
                onClick={() => onClickSelectedSymptomOther(value)}
              />
            ))}
          </UiSimpleGrid>
          {renderSymptomInput()}
        </Col>
      </UiQuestionnaireLayout>
    </>
  );
};

export default Questionnaire8Screen;
